import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardHeader, CardTitle, CardContent, Skeleton } from '@pagespeedify/ui';
import { useAuth, ErrorBoundary } from '../lib/auth';
import { useApi } from '../lib/apiClient';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Zap, FileText, ChevronLeft, ChevronRight, Activity, UserPlus } from 'lucide-react';
import { ReportUsageResponse } from '../types/reports';
import { Task } from '../types';
import InsightsTabsCard from './InsightsTabsCard';
import PerformanceTrendChart from './dashboard/PerformanceTrendChart';
import AgentPromptCard from './dashboard/AgentPromptCard';
import { useTour } from '../context/TourContext';
import DashboardTour from './tour/DashboardTour';

const GITHUB_OAUTH_RETURN_KEY = 'swayashin_github_oauth_return';

export default function UserDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const apiClient = useApi();
  const { startPageTour, isPageTourSeen } = useTour();

  // State for pagination
  const [aiTasksPage, setAiTasksPage] = React.useState(0);
  const [reportsPage, setReportsPage] = React.useState(0);
  const [monitoringPage, setMonitoringPage] = React.useState(0);

  // State for filters
  const [aiTaskStatusFilter, setAiTaskStatusFilter] = React.useState<string>('all');
  const [aiTaskPriorityFilter, setAiTaskPriorityFilter] = React.useState<string>('all');
  const [reportsStatusFilter, setReportsStatusFilter] = React.useState<string>('all');
  const [reportsPriorityFilter, setReportsPriorityFilter] = React.useState<string>('all');
  const [monitoringStatusFilter, setMonitoringStatusFilter] = React.useState<string>('all');
  const [monitoringPriorityFilter, setMonitoringPriorityFilter] = React.useState<string>('all');

  // Debug trace to help track render issues in production/dev
  // This log helps confirm whether we're rendering for an unsubscribed user
  React.useEffect(() => {
    if (user) {
      console.log('[UserDashboard] User loaded:', user.email, user.user_type);
    }
  }, [user]);

  // Start dashboard tour if not seen yet
  React.useEffect(() => {
    if (user && !isPageTourSeen('dashboard')) {
      setTimeout(() => startPageTour('dashboard'), 1000);
    }
  }, [user, startPageTour, isPageTourSeen]);

  React.useEffect(() => {
    // Clear stale fallback marker when callback already returned directly to dashboard.
    try {
      const raw = sessionStorage.getItem(GITHUB_OAUTH_RETURN_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { path?: string };
      if (parsed?.path === '/user-dashboard') {
        sessionStorage.removeItem(GITHUB_OAUTH_RETURN_KEY);
      }
    } catch (error) {
      console.warn('[UserDashboard] Failed to clear OAuth return path marker:', error);
      sessionStorage.removeItem(GITHUB_OAUTH_RETURN_KEY);
    }
  }, []);

  // Early return if user is not loaded yet
  if (!user) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Defensive: if api client is missing, show helpful message instead of letting queries throw
  if (!apiClient) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="text-center py-10">
          <p className="text-sm text-muted-foreground">Failed to load dashboard: API client unavailable.</p>
        </div>
      </div>
    );
  }

  // Determine effective manifest owner ID (ReportsHistory logic)


  const { data: reportsHistory, isLoading: isLoadingReports, error: reportsError } = useQuery<any[], Error>({
    queryKey: ['ReportsHistory', user?.internalUserId],
    queryFn: () => apiClient.get<any[]>('/reports'),
    enabled: !!user?.uid,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 403/404 errors
      if (error?.message?.includes('403') || error?.message?.includes('404')) return false;
      return failureCount < 2;
    },
  });

  const { data: reportUsage, isLoading: isLoadingReportUsage } = useQuery<ReportUsageResponse, Error>({
    queryKey: ['reportUsage', user?.uid],
    queryFn: async () => {
      if (!apiClient) throw new Error('API client not available');
      const response = await apiClient.get<{ success: boolean, usage: ReportUsageResponse, message?: string }>('/reports/usage');
      if (!response.success || !response.usage) {
        throw new Error(response.message || 'Failed to fetch usage data.');
      }
      return response.usage;
    },
    enabled: !!user && !!apiClient,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error?.message?.includes('403') || error?.message?.includes('404')) return false;
      return failureCount < 2;
    },
  });

  const { data: tasksData, isLoading: isLoadingTasks } = useQuery<{ data: { tasks: { tasks: Task[] } } }, Error>({
    queryKey: ['ai-tasks-list'],
    queryFn: () => apiClient.get('/tasks'),
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      if (error?.message?.includes('403') || error?.message?.includes('404')) return false;
      return failureCount < 2;
    },
  });



  // Fetch monitoring data for dashboard insights
  interface MonitoredUrl {
    id: string;
    website_url: string;
    is_active: boolean;
    status?: string;
    priority?: string;
    device_type?: string;
    additional_metrics?: {
      breach_state?: Record<string, { breached?: boolean }>;
    };
  }
  const { data: monitoringData, isLoading: isLoadingMonitoring } = useQuery<{ data: MonitoredUrl[] }, Error>({
    queryKey: ['monitoring-dashboard'],
    queryFn: () => apiClient.get('/monitoring'),
    enabled: !!user && !!apiClient,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      if (error?.message?.includes('403') || error?.message?.includes('404')) return false;
      return failureCount < 2;
    },
  });

  // Fetch team owner's credits if user is a team member
  // Note: User object already contains team information from auth context, no additional API call needed

  // Helper function to render error states




  const tasksCompleted = React.useMemo(() => {
    if (!tasksData?.data?.tasks?.tasks) return 0;
    return tasksData.data.tasks.tasks.filter(t => t.status === 'resolved' || t.status === 'closed').length;
  }, [tasksData]);

  const tasksPending = React.useMemo(() => {
    if (!tasksData?.data?.tasks?.tasks) return 0;
    return tasksData.data.tasks.tasks.filter(t => t.status === 'open' || t.status === 'in_progress').length;
  }, [tasksData]);

  // Monitoring stats
  const monitoringStats = React.useMemo(() => {
    const urls = monitoringData?.data || [];
    const activeUrls = urls.filter(u => u.is_active);
    let urlsWithBreaches = 0;
    let healthy = 0;
    let warning = 0;
    let critical = 0;

    activeUrls.forEach(url => {
      const breachState = url.additional_metrics?.breach_state || {};
      const breachCount = Object.values(breachState).filter(s => s?.breached).length;

      if (breachCount === 0) {
        healthy++;
      } else if (breachCount <= 2) {
        warning++;
        urlsWithBreaches++;
      } else {
        critical++;
        urlsWithBreaches++;
      }
    });

    return {
      total: urls.length,
      active: activeUrls.length,
      urlsWithBreaches,
      good: healthy,
      needsImprovement: warning,
      poor: critical,
    };
  }, [monitoringData]);

  // Paginated monitoring URLs for the insights card




  // Determine which subscription type to display for team scenarios
  const displaySubscriptionType = React.useMemo(() => {
    // For team owners, check granted perks for effective subscription
    if (user?.team_id && user?.team_owner_id === user?.internalUserId) {
      if (user?.granted_plan_tier_activation_perks?.includes('enterprise')) {
        return 'enterprise';
      }
      if (user?.granted_plan_tier_activation_perks?.includes('pro')) {
        return 'pro';
      }
      if (user?.granted_plan_tier_activation_perks?.includes('basic')) {
        return 'basic';
      }
    }
    // For all users (team members or individual), show their effective subscription type
    // The user object from auth context should already have the correct subscription
    return user?.user_type || 'Unknown';
  }, [user]);

  // Determine which credits to display for team scenarios
  const displayCredits = React.useMemo(() => {
    // The user object from auth context should already have the correct credits
    return user?.credits ?? 0;
  }, [user]);





  // New filtered task lists for dashboard sections
  const tasksList = React.useMemo(() => {
    return tasksData?.data?.tasks?.tasks || [];
  }, [tasksData]);




  // Robustly extract a team name from the user object, checking common shapes
  const teamNameFromUser = React.useMemo(() => {
    const u: any = user as any;
    if (!u || typeof u !== 'object') return null;

    const candidates: Array<string | null | undefined> = [];
    // common top-level keys
    candidates.push(u.team_name, u.teamName, u.team?.name, u.team?.team_name);
    // common membership shapes
    candidates.push(u.teamInfo?.name, u.teamInfo?.team_name, u.team_details?.name);
    // arrays where user may have teams
    if (Array.isArray(u.teams) && u.teams.length > 0) {
      candidates.push(u.teams[0]?.name, u.teams[0]?.team_name);
    }
    if (Array.isArray(u.member_of) && u.member_of.length > 0) {
      candidates.push(u.member_of[0]?.name, u.member_of[0]?.team_name);
    }
    // nested membership object
    candidates.push(u.team_membership?.team?.name, u.team_membership?.team?.team_name);

    for (const c of candidates) {
      if (typeof c === 'string') {
        const t = c.trim();
        if (t.length > 0) return t;
      }
    }

    return null;
  }, [user]);

  // Only fetch /teams when the authenticated user is a team owner and no team name is present
  const { data: teamsResponse } = useQuery<any, Error>({
    queryKey: ['userTeams', user?.uid],
    queryFn: () => apiClient.get('/teams'),
    enabled: !!user && !teamNameFromUser && !!apiClient && user?.team_owner_id === user?.internalUserId,
    staleTime: 5 * 60 * 1000,
  });

  const teamName = React.useMemo(() => {
    if (teamNameFromUser) return teamNameFromUser;
    try {
      // teamsResponse may be { success, data: [...] } or an array
      const anyResp = teamsResponse as any;
      const list = Array.isArray(anyResp) ? anyResp : anyResp?.data ?? anyResp?.teams ?? [];
      if (!list || list.length === 0) return null;
      // If user.team_id is present, find that team
      if (user?.team_id) {
        const found = list.find((t: any) => String(t.id) === String(user.team_id) || String(t.id) === String(user.team_id));
        if (found) return found.name || found.team_name || null;
      }
      // Fallback to first team in list
      return list[0].name || list[0].team_name || null;
    } catch (e) {
      return null;
    }
  }, [teamNameFromUser, teamsResponse, user]);


  return (
    <ErrorBoundary>
      <div className="p-4 md:p-6 lg:p-8">
        {/* Welcome Section with Inline Quick Actions */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!</h1>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span><strong>{displayCredits}</strong> credits</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium capitalize">
                {displaySubscriptionType}
              </span>
            </div>
            {user?.team_id && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">
                  {user?.team_owner_id === user?.internalUserId ? (
                    <span>Team Owner{teamName ? ` of ${teamName}` : ''}</span>
                  ) : (
                    <span>Team Member{teamName ? ` of ${teamName}` : ''}</span>
                  )}
                </span>
              </div>
            )}
            {/* Inline Quick Actions */}
            <div className="flex items-center gap-2 ml-auto">
              <Link to="/reports">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                  <FileText className="w-3 h-3 text-blue-500" /> New Audit
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => navigate('/tasks')}
              >
                <Briefcase className="w-3 h-3 text-purple-500" /> New Task
              </Button>
              <Link to="/credits">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                  <Zap className="w-3 h-3 text-green-500" /> Add Credits
                </Button>
              </Link>
              <Link to="/manage-team">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                  <UserPlus className="w-3 h-3 text-orange-500" /> Invite Team
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Agent Prompt Card - Primary action for users */}
        <div className="mb-6">
          <AgentPromptCard />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-tour="dashboard-stats">
          {/* Stats Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Monitored URLs</CardTitle>
              <Activity className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingMonitoring ? <Skeleton className="h-8 w-1/2" /> : (
                <div className="text-2xl font-bold">
                  {monitoringStats.active}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {monitoringStats.total > monitoringStats.active ? `${monitoringStats.total - monitoringStats.active} paused` : 'Active monitoring'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Reports Generated</CardTitle>
              <FileText className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingReportUsage ? <Skeleton className="h-8 w-1/2" /> : (
                <div className="text-2xl font-bold">
                  {(reportUsage?.consumedInPeriod || 0)}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                This billing period
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Tasks Completed</CardTitle>
              <Briefcase className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingTasks ? <Skeleton className="h-8 w-1/2" /> : (
                <div className="text-2xl font-bold">
                  {tasksCompleted}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {tasksPending > 0 ? `${tasksPending} pending` : 'All time'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Agent Tasks</CardTitle>
              <Zap className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingTasks ? <Skeleton className="h-8 w-1/2" /> : (
                <div className="text-2xl font-bold">
                  {tasksList.filter(t => t.type === 'agent').length}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Total agent tasks
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Trend Chart - Full Width with margin */}
        <div className="mt-8 mb-6">
          <PerformanceTrendChart reports={reportsHistory || []} />
        </div>

        {/* Unified Insights Tabs Card */}
        <div className="mt-6 overflow-hidden max-w-full" data-tour="insights-section">
          <InsightsTabsCard
            tasks={tasksData?.data?.tasks?.tasks || []}
            isLoadingTasks={isLoadingTasks}
            aiTaskStatusFilter={aiTaskStatusFilter}
            setAiTaskStatusFilter={setAiTaskStatusFilter}
            aiTaskPriorityFilter={aiTaskPriorityFilter}
            setAiTaskPriorityFilter={setAiTaskPriorityFilter}
            aiTasksPage={aiTasksPage}
            setAiTasksPage={setAiTasksPage}
            reports={reportsHistory || []}
            isLoadingReports={isLoadingReports}
            reportsStatusFilter={reportsStatusFilter}
            setReportsStatusFilter={setReportsStatusFilter}
            reportsPriorityFilter={reportsPriorityFilter}
            setReportsPriorityFilter={setReportsPriorityFilter}
            reportsPage={reportsPage}
            setReportsPage={setReportsPage}
            monitoringUrls={monitoringData?.data || []}
            isLoadingMonitoring={isLoadingMonitoring}
            monitoringStatusFilter={monitoringStatusFilter}
            setMonitoringStatusFilter={setMonitoringStatusFilter}
            monitoringPriorityFilter={monitoringPriorityFilter}
            setMonitoringPriorityFilter={setMonitoringPriorityFilter}
            monitoringPage={monitoringPage}
            setMonitoringPage={setMonitoringPage}
            user={user}
          />
        </div>
      </div>
      <DashboardTour />
    </ErrorBoundary>
  );
}
